import { function as F } from "fp-ts";
import { done } from "./original/exit";
import * as T from "./effect";
import { effect } from "./effect";
export var ManagedTag;
(function (ManagedTag) {
    ManagedTag[ManagedTag["Pure"] = 0] = "Pure";
    ManagedTag[ManagedTag["Encase"] = 1] = "Encase";
    ManagedTag[ManagedTag["Bracket"] = 2] = "Bracket";
    ManagedTag[ManagedTag["Suspended"] = 3] = "Suspended";
    ManagedTag[ManagedTag["Chain"] = 4] = "Chain";
    ManagedTag[ManagedTag["BracketExit"] = 5] = "BracketExit";
})(ManagedTag || (ManagedTag = {}));
const toM = (_) => _;
const fromM = (_) => _;
/**
 * Lift a pure value into a resource
 * @param value
 */
export function pure(value) {
    return toM(() => ({
        _tag: ManagedTag.Pure,
        value
    }));
}
/**
 * Create a Resource by wrapping an IO producing a value that does not need to be disposed
 *
 * @param res
 * @param f
 */
export function encaseEffect(rio) {
    return toM((r) => ({
        _tag: ManagedTag.Encase,
        acquire: T.provideAll(r)(rio)
    }));
}
/**
 * Create a resource from an acquisition and release function
 * @param acquire
 * @param release
 */
export function bracket(acquire, release) {
    return toM((r) => ({
        _tag: ManagedTag.Bracket,
        acquire: T.provideAll(r)(acquire),
        release: (a) => T.provideAll(r)(release(a))
    }));
}
export function bracketExit(acquire, release) {
    return toM((r) => ({
        _tag: ManagedTag.BracketExit,
        acquire: T.provideAll(r)(acquire),
        release: (a, e) => T.provideAll(r)(release(a, e))
    }));
}
/**
 * Lift an IO of a Resource into a resource
 * @param suspended
 */
export function suspend(suspended) {
    return toM((r) => ({
        _tag: ManagedTag.Suspended,
        suspended: effect.map(T.provideAll(r)(suspended), (m) => (_) => fromM(m)(r))
    }));
}
/**
 * Compose dependent resourcess.
 *
 * The scope of left will enclose the scope of the resource produced by bind
 * @param left
 * @param bind
 */
function chain_(left, bind) {
    return toM((r) => ({
        _tag: ManagedTag.Chain,
        left: provideAll(r)(left),
        bind: (l) => provideAll(r)(bind(l))
    }));
}
/**
 * Curried form of chain
 * @param bind
 */
export function chain(bind) {
    return (left) => chain_(left, bind);
}
/**
 * Map a resource
 * @param res
 * @param f
 */
function map_(res, f) {
    return chain_(res, (r) => pure(f(r)));
}
/**
 * Curried form of map
 * @param f
 */
export function map(f) {
    return (res) => map_(res, f);
}
/**
 * Zip two resources together with the given function.
 *
 * The scope of resa will enclose the scope of resb
 * @param resa
 * @param resb
 * @param f
 */
export function zipWith(resa, resb, f) {
    return chain_(resa, (a) => map_(resb, (b) => f(a, b)));
}
/**
 * Zip two resources together as a tuple.
 *
 * The scope of resa will enclose the scope of resb
 * @param resa
 * @param resb
 */
export function zip(resa, resb) {
    return zipWith(resa, resb, (a, b) => [a, b]);
}
/**
 * Apply the function produced by resfab to the value produced by resa to produce a new resource.
 * @param resa
 * @param resfab
 */
export function ap(resa, resfab) {
    return zipWith(resa, resfab, (a, f) => f(a));
}
/**
 * Flipped version of ap
 * @param resfab
 * @param resa
 */
function ap_(resfab, resa) {
    return zipWith(resfab, resa, (f, a) => f(a));
}
/**
 * Map a resource to a static value
 *
 * This creates a resource of the provided constant b where the produced A has the same lifetime internally
 * @param fa
 * @param b
 */
export function as(fa, b) {
    return map_(fa, F.constant(b));
}
/**
 * Curried form of as
 * @param b
 */
export function to(b) {
    return (fa) => as(fa, b);
}
/**
 * Construct a new 'hidden' resource using the produced A with a nested lifetime
 * Useful for performing initialization and cleanup that clients don't need to see
 * @param left
 * @param bind
 */
export function chainTap(left, bind) {
    return chain_(left, (a) => as(bind(a), a));
}
/**
 * Curried form of chainTap
 * @param bind
 */
export function chainTapWith(bind) {
    return (inner) => chainTap(inner, bind);
}
/**
 * Curried data last form of use
 * @param f
 */
export function consume(f) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return (r) => use(r, f);
}
/**
 * Create a Resource from the fiber of an IO.
 * The acquisition of this resource corresponds to forking rio into a fiber.
 * The destruction of the resource is interrupting said fiber.
 * @param rio
 */
export function fiber(rio) {
    return bracket(T.fork(rio), (fiber) => fiber.interrupt);
}
/**
 * Use a resource to produce a program that can be run.s
 * @param res
 * @param f
 */
export function use(res, f) {
    return T.accessM((r) => {
        const c = fromM(res)(r);
        switch (c._tag) {
            case ManagedTag.Pure:
                return f(c.value);
            case ManagedTag.Encase:
                return effect.chain(c.acquire, f);
            case ManagedTag.Bracket:
                return T.bracket(c.acquire, c.release, f);
            case ManagedTag.BracketExit:
                return T.bracketExit(c.acquire, (a, e) => c.release(a, e), f);
            case ManagedTag.Suspended:
                return effect.chain(c.suspended, consume(f));
            case ManagedTag.Chain:
                return use(c.left, (a) => use(c.bind(a), f));
        }
    });
}
/**
 * Create an IO action that will produce the resource for this managed along with its finalizer
 * action seperately.
 *
 * If an error occurs during allocation then any allocated resources should be cleaned up, but once the
 * Leak object is produced it is the callers responsibility to ensure release is invoked.
 * @param res
 */
export function allocate(res) {
    return T.accessM((r) => {
        const c = fromM(res)(r);
        switch (c._tag) {
            case ManagedTag.Pure:
                return T.pure({ a: c.value, release: T.unit });
            case ManagedTag.Encase:
                return effect.map(c.acquire, (a) => ({ a, release: T.unit }));
            case ManagedTag.Bracket:
                return effect.map(c.acquire, (a) => ({ a, release: c.release(a) }));
            case ManagedTag.BracketExit:
                // best effort, because we cannot know what the exit status here
                return effect.map(c.acquire, (a) => ({
                    a,
                    release: c.release(a, done(undefined))
                }));
            case ManagedTag.Suspended:
                return effect.chain(c.suspended, allocate);
            case ManagedTag.Chain:
                return T.bracketExit(allocate(c.left), (leak, exit) => (exit._tag === "Done" ? T.unit : leak.release), (leak) => effect.map(allocate(c.bind(leak.a)), 
                // Combine the finalizer actions of the outer and inner resource
                (innerLeak) => ({
                    a: innerLeak.a,
                    release: T.effect.onComplete(innerLeak.release, leak.release)
                })));
        }
    });
}
/**
 * Use a resource to provide part of the environment to an effect
 * @param man
 * @param ma
 */
export function provideS(man) {
    return (ma) => T.accessM((_) => use(man, (r) => T.provideAll(Object.assign(Object.assign({}, r), _))(ma)));
}
export const URI = "matechs/Managed";
export const managed = {
    URI,
    of: pure,
    map: map_,
    ap: ap_,
    chain: chain_
};
export function getSemigroup(Semigroup) {
    return {
        concat(x, y) {
            return zipWith(x, y, Semigroup.concat);
        }
    };
}
export function getMonoid(Monoid) {
    return Object.assign(Object.assign({}, getSemigroup(Monoid)), { empty: pure(Monoid.empty) });
}
export function provideAll(r) {
    return (ma) => toM(() => fromM(ma)(r));
}
